import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import pageStyles from './BranchDetail.module.css';
import styles from './CciJobDetail.module.css';
import Card from '../components/common/Card';
import JsonTree from '../components/common/JsonTree';
import ListPageHeader from '../components/common/ListPageHeader';
import Pill from '../components/common/Pill';
import ProfileLogDrawer from '../components/cci/ProfileLogDrawer';
import {
  cciStatusKind, cciStatusLabel, commitUrl, formatSeconds, prUrl, runStateKind, shortSha, triggerLabel,
} from '../components/cci/cciStatus';
import { apiGetCciJob } from '../api/cci';
import { formatDateTime } from '../helpers/dates';
import { WebSocketContext } from '../api/socket';

const CCI_COMPONENT_REPOS = {
  collate: 'open-metadata/openmetadata-collate',
  OpenMetadata: 'open-metadata/OpenMetadata',
  'ai-platform': 'open-metadata/ai-platform',
};

const Field = ({ label, children }) => (
  <>
    <dt className={styles.label}>{label}</dt>
    <dd className={styles.value}>{children ?? <span className={styles.muted}>—</span>}</dd>
  </>
);

const ExternalLink = ({ href, children }) => (
  <a href={href} target="_blank" rel="noreferrer" className={styles.link}>{children}</a>
);

function EventCard({ job }) {
  const { event } = job;
  const pr = prUrl(event);
  return (
    <Card title="Event">
      <dl className={styles.fields}>
        <Field label="Trigger">{triggerLabel(event)}</Field>
        <Field label="Repository">{event.repo}</Field>
        <Field label="Pull request">{pr && <ExternalLink href={pr}>#{event.pr_number}</ExternalLink>}</Field>
        <Field label="Head commit">
          <ExternalLink href={commitUrl(event.repo, event.head_sha)}>
            <span className={styles.mono}>{event.head_sha}</span>
          </ExternalLink>
        </Field>
        <Field label="Base">{event.base_ref}</Field>
        <Field label="Event time">{formatDateTime(event.event_time)}</Field>
        <Field label="Labels">{job.labels.length ? job.labels.join(', ') : null}</Field>
      </dl>
    </Card>
  );
}

function QueueCard({ job }) {
  return (
    <Card title="Queue">
      <dl className={styles.fields}>
        <Field label="Attempt">{job.attempt} of {job.max_attempts}</Field>
        <Field label="Lease expires">{job.lease_expires_at && formatDateTime(job.lease_expires_at)}</Field>
        <Field label="Runner">{job.runner_id && <span className={styles.mono}>{job.runner_id}</span>}</Field>
        <Field label="Runner labels">{job.runner_labels.length ? job.runner_labels.join(', ') : null}</Field>
        <Field label="Profiles">{job.profiles.join(', ')}</Field>
        <Field label="Required">{job.required ? 'Yes' : 'No'}</Field>
        <Field label="Timeout">{formatSeconds(job.timeout_seconds)}</Field>
        <Field label="Created">{formatDateTime(job.created_at)}</Field>
        <Field label="Updated">{formatDateTime(job.updated_at)}</Field>
      </dl>
    </Card>
  );
}

function ComponentsCard({ components }) {
  return (
    <Card title="Components" noPadding>
      <table className={styles.table}>
        <tbody>
          {Object.entries(components).map(([name, sha]) => (
            <tr key={name}>
              <td>{name}</td>
              <td className={styles.mono}>
                {CCI_COMPONENT_REPOS[name]
                  ? <ExternalLink href={commitUrl(CCI_COMPONENT_REPOS[name], sha)}>{sha}</ExternalLink>
                  : sha}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function ReportCard({ jobId, report }) {
  const [openProfile, setOpenProfile] = useState(null);
  if (!report) {
    return (
      <Card title="Runner report">
        <span className={styles.muted}>No status reported yet.</span>
      </Card>
    );
  }
  return (
    <Card
      title={`Runner report · seq ${report.seq}`}
      action={<Pill kind={runStateKind(report.state)} dot pulse={report.state === 'running'}>{report.state}</Pill>}
    >
      <dl className={styles.fields}>
        <Field label="Finished">{report.finished_at && formatDateTime(report.finished_at)}</Field>
        <Field label="Log">{report.log_url && <span className={styles.mono}>{report.log_url}</span>}</Field>
        <Field label="Error">{report.error && <span className={styles.error}>{report.error}</span>}</Field>
      </dl>
      {report.profiles.length > 0 && (
        <table className={styles.table}>
          <thead>
            <tr><th>Profile</th><th>Status</th><th>Duration</th><th>Cached</th></tr>
          </thead>
          <tbody>
            {report.profiles.map((profile) => (
              <tr key={profile.name}>
                <td>
                  {profile.log_url || profile.error
                    ? <button type="button" className={styles.linkButton} onClick={() => setOpenProfile(profile.name)}>
                      {profile.name}
                    </button>
                    : profile.name}
                </td>
                <td><Pill kind={runStateKind(profile.status)}>{profile.status}</Pill></td>
                <td>{formatSeconds(profile.seconds)}</td>
                <td>{profile.cached ? 'Yes' : 'No'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {report.artifacts.length > 0 && (
        <table className={styles.table}>
          <thead>
            <tr><th>Artifact</th><th>Location</th></tr>
          </thead>
          <tbody>
            {report.artifacts.map((artifact) => (
              <tr key={artifact.name}>
                <td>{artifact.name}</td>
                <td className={styles.mono}>{artifact.s3_url}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <ProfileLogDrawer
        jobId={jobId}
        profile={report.profiles.find((p) => p.name === openProfile)}
        onClose={() => setOpenProfile(null)}
      />
    </Card>
  );
}

export default function CciJobDetail({ user }) {
  const { job_id } = useParams();
  const ws = useContext(WebSocketContext);
  const [job, setJob] = useState({ loaded: false });

  const reloadData = () => {
    apiGetCciJob(job_id).then((r) => setJob({ loaded: true, data: r }))
      .catch((e) => setJob({ loaded: true, error: e }));
  };

  useEffect(() => {
    reloadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, job_id]);

  useEffect(() => {
    if (!ws) return undefined;
    const onJobUpdate = ({ job: updated }) => {
      if (updated.job_id === job_id) setJob({ loaded: true, data: updated });
    };
    ws.on('cci_job_update', onJobUpdate);
    return () => { ws.off('cci_job_update', onJobUpdate); };
  }, [ws, job_id]);

  if (!job.loaded) {
    return (
      <div className={pageStyles.loading}>
        <div className={pageStyles.loadingSpinner} />
        Loading job…
      </div>
    );
  }
  if (job.error) {
    return (
      <div className={pageStyles.errorPage}>
        <div className={pageStyles.errorTitle}>Could not load job {job_id}</div>
        <div className={pageStyles.errorMsg}>{String(job.error.detail ?? job.error)}</div>
      </div>
    );
  }

  const data = job.data;
  return (
    <div className={pageStyles.page}>
      <div className={pageStyles.inner}>
        <ListPageHeader
          title={<span className={styles.title}>Job <span className={styles.mono}>{data.job_id}</span>
            <Pill kind={cciStatusKind(data.status)} dot pulse={data.status === 'leased'}>{cciStatusLabel(data.status)}</Pill>
          </span>}
          subtitle={`${data.event.repo}${data.event.pr_number ? ` #${data.event.pr_number}` : ''} @ ${shortSha(data.event.head_sha)}`}
          onReload={reloadData}
        />
        <div className={styles.grid}>
          <EventCard job={data} />
          <QueueCard job={data} />
        </div>
        <ComponentsCard components={data.components} />
        <ReportCard jobId={data.job_id} report={data.report} />
        <Card title="Raw">
          <JsonTree data={data} />
        </Card>
      </div>
    </div>
  );
}
